import { Injectable } from '@angular/core';
import {Capacitor} from "@capacitor/core";
import * as Sentry from "@sentry/capacitor";
import * as SentryAngular from "@sentry/angular";
import {ApiRequestService} from "./api-request.service";
import {environment} from "../../environments/environment";
import {UtilsService} from "./utils.service";
import { AppService } from './app.service';

@Injectable({
  providedIn: 'root'
})
export class SentryService {

  isNative: boolean = Capacitor.isNativePlatform(); // true if iOS/Android app

  constructor(
    private api: ApiRequestService,
    private utils: UtilsService,
    private app: AppService
  ) { }

  /**
   * Set user information and tag with user_id in Sentry.
   *
   * @param {any} value - The user id value to set.
   * @return {void}
   */
  public setUser(value: any) {
    Sentry.setUser({
      id: value
    });
    Sentry.setTag('user_id', value);
  }

  /**
   * Set the employer in Sentry context and tags.
   *
   * @param {any} value - The ID of the employer to set.
   * @return {void}
   */
  public setEmployer(value: any) {
    Sentry.setContext('employer', {
      id: value
    });
    Sentry.setTag('employer_id', value);
  }

  /**
   * Set the site context and tag in Sentry.
   *
   * @param {any} value - The value to set as the site identifier.
   * @return {void}
   */
  public setSite(value: any) {
    Sentry.setContext('site', {
      id: value
    });
    Sentry.setTag('site_id', value);
  }

  /**
   * Perform a test error handling scenario based on the provided type.
   *
   * @param {string} type - The type of the error scenario to test. Possible values include:
   *   - 'manual': Test manual error captured using Sentry.captureException()
   *   - 'uncaught': Test an uncaught error (should be captured by Angular ErrorHandler)
   *   - 'promise': Test an unhandled promise rejection
   *   - 'http': Test making an HTTP request and handling the response/error
   *   - 'custom': Test throwing a custom error extending Error class
   *   - 'native': If native client is available it will trigger a native crash
   *   - 'console': Test logging error using console.error
   *   - 'api-runtime', 'api-validation', 'api-query', 'api-queue', 'api-s3', 'api-custom': Test making an API request with specified API type
   *
   * @return {void} - No return value. Throws errors and logs messages based on the provided error type.
   */
  public test(type: string) {
    const apiType = type.replace(/^api-/, ''); // Removes 'api-' prefix if present
    switch (type) {
      case 'manual':
        try {
          throw new Error('Manual error captured using Sentry.captureException()');
        } catch (err) {
          Sentry.captureException(err);
        }
        break;

      case 'uncaught':
        throw new Error('Uncaught error (should be captured by Angular ErrorHandler)');

      case 'promise':
        Promise.reject(new Error('Unhandled promise rejection'));
        break;

      case 'http':
        this.api.makeRequest('get', `v2/unknown`, {}, {})
          .then((response) => {
            console.log(response);
          })
          .catch((_errorResponse) => {
            Sentry.captureException(_errorResponse);
          });
        break;

      case 'custom':
      class CustomSentryError extends Error {
        constructor() {
          super('CustomSentryError triggered');
          this.name = 'CustomSentryError';
        }
      }
        throw new CustomSentryError();

      case 'native':
        if ( this.isNative ) {
          Sentry.nativeCrash(); // Only works if @sentry/capacitor is properly initialized
        } else {
          this.utils.showMessage('Error', 'You need to run this app on a mobile device.');
        }
        break;

      case 'console':
        console.error('Console.error only – not captured unless redirected');
        break;

      case 'api-runtime':
      case 'api-validation':
      case 'api-query':
      case 'api-queue':
      case 'api-s3':
      case 'api-custom':
        this.api.makeRequest('get', `v2/sentry/throw-error/${apiType}`, {}, {})
          .then((response) => {
            console.log(response);
          })
          .catch((_errorResponse) => {
            console.error(_errorResponse);
          });
        break;

      default:
        console.warn(`Unknown error type: ${type}`);
    }
  }

  /**
   * Method to check if initialization is allowed by making a request to the server.
   *
   * @return {Promise<any>} A Promise indicating if initialization is allowed.
   */
  public allowInit() {
    return this.api.makeRequest('get', `v2/sentry/is-allowed`)
      .then((response: any) => {
        return response;
      })
      .catch((errorResponse) => {
        console.error(errorResponse?.error?.message || 'Failed to check Sentry status.');
        return false; // fallback to false on error
      });
  }

  /**
   * Initializes the Sentry SDK with the provided configuration options.
   *
   * @return {void}
   */
  public onInit() {
    Sentry.init({
        dsn: environment.SENTRY_DSN, // Replace with your DSN
        environment: environment.SENTRY_ENVIRONMENT, // or 'dev', 'staging', 'production'
        release: environment.SENTRY_RELEASE, // Set your release version, such as "getsentry@1.0.0"
        // dist: '<dist>', // optional build/dist identifier
        integrations: [
          // Registers and configures the Tracing integration,
          // which automatically instruments your application to monitor its
          // performance, including custom Angular routing instrumentation
          Sentry.browserTracingIntegration()
          // Session Replay is intentionally not registered. The replay integration
          // pulls roughly 0.3 MB into the initial bundle and its value on a fixed
          // kiosk device is limited. Re-add Sentry.replayIntegration() here (and
          // restore the replay sample rates below) if replays are needed again.
        ],
        // Set tracesSampleRate to 0.1 to capture 10% of transactions for tracing.
        // Learn more at https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
        tracesSampleRate: environment.SENTRY_TRACES_SAMPLE_RATE,
        // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
        tracePropagationTargets: [
          /^\/.*/,
          this.app.getAPIEndpointURL('') // Leave empty for base URL.
        ],
        // The replay sample rate options (SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
        // SENTRY_NATIVE_REPLAYS_SESSION_SAMPLE_RATE, SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE)
        // are omitted along with the replay integration above. The environment values
        // are still defined, so restoring replays only needs these two options back.
        beforeSend(event, hint) {
          const eventLevel = event.level || 'error';
          const allowedEventLevels = ['error', 'fatal']; // Only send these and ignore other levels ('warning', 'log', 'info', 'debug')
          if (!allowedEventLevels.includes(eventLevel)) {
            return null; // Drop this event
          }

          const error = hint?.originalException;
          // List of known error messages to ignore
          const ignoredMessages = [
            'not implemented on web'
          ];
          if (error instanceof Error && ignoredMessages.some(msg => error.message?.includes(msg))) {
            return null; // Ignore this error
          }

          return event;
        }
      },
      SentryAngular.init
    );
  }

}
